import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { Search, Store, Clock, ImagePlus, Trash2, RotateCcw, Pencil, MapPin, EyeOff, Smartphone, GitMerge, X, Building2, Undo2, Receipt, Crown } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../i18n/I18nProvider';
import { useSession } from '../../session/SessionProvider';
import { errorText } from '../../lib/errors';
import { parseSchedule } from '../../lib/schedule';
import { PageHeader, Card, CardBody, Input, Textarea, Field, Button, Badge, Modal, ConfirmDialog, EmptyState, LoadingBlock, Tabs, Switch, Select } from '../../ui';

const MAX_LOGO = 1024 * 1024;
const LOGO_TYPES = 'image/png,image/jpeg,image/svg+xml,image/webp';

const TOP_PERIODS = [30, 90, 180, 365, 0];

/** «Найпопулярніший заклад» у застосунку: за який період рахувати і, за потреби, лідер вручну */
function TopVenueCard({ tenantId, canEdit, version }) {
  const { t, fmt } = useI18n();
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/tenants/${tenantId}/top-venue`)
      .then((res) => setData(res.data.data))
      .catch(() => setData(null));
  }, [tenantId]);
  useEffect(() => { load(); }, [load, version]);

  if (!data) return null;
  const nameOf = (id) => data.venues.find((v) => v.id === id)?.name;
  const visitsOf = (id) => data.venues.find((v) => v.id === id)?.visits || 0;

  const save = async (next) => {
    setSaving(true);
    try {
      await api.put(`/admin/tenants/${tenantId}/top-venue`, { days: data.days, venue_id: data.venue_id, ...next });
      toast.success(t('common.saved'));
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const periodLabel = (d) => (d ? t('venues.top.days', { count: d }) : t('venues.top.allTime'));
  const autoName = nameOf(data.auto_venue_id);

  return (
    <Card className="mb-5">
      <CardBody>
        <div className="flex items-start gap-3">
          <Crown className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('venues.top.title')}</p>
            <p className="mt-0.5 max-w-2xl text-xs text-zinc-500">{t('venues.top.hint')}</p>
            <p className="mt-2 text-sm text-zinc-700 dark:text-zinc-300">
              {data.current_venue_id ? (
                <>
                  {t('venues.top.now')}: <span className="font-semibold">{nameOf(data.current_venue_id)}</span>
                  {' · '}
                  <span className="tabular text-zinc-500">{t('venues.top.visits', { count: fmt.number(visitsOf(data.current_venue_id)) })} {periodLabel(data.days)}</span>
                  {data.venue_id ? <Badge tone="warning" className="ml-2">{t('venues.top.manualBadge')}</Badge> : null}
                </>
              ) : t('venues.top.none')}
            </p>
            <div className="mt-3 grid gap-3 sm:grid-cols-2 sm:max-w-2xl">
              <Field label={t('venues.top.period')}>
                <Select value={String(data.days)} onChange={(e) => save({ days: Number(e.target.value) })} disabled={!canEdit || saving}>
                  {TOP_PERIODS.map((d) => <option key={d} value={d}>{periodLabel(d)}</option>)}
                </Select>
              </Field>
              <Field label={t('venues.top.leader')}>
                <Select value={data.venue_id ? String(data.venue_id) : ''} onChange={(e) => save({ venue_id: e.target.value ? Number(e.target.value) : null })} disabled={!canEdit || saving}>
                  <option value="">{autoName ? t('venues.top.autoWith', { name: autoName }) : t('venues.top.auto')}</option>
                  {data.venues.map((v) => (
                    <option key={v.id} value={v.id}>{v.name} · {t('venues.top.visits', { count: fmt.number(v.visits) })}</option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

/** Як застосунок зрозумів графік: «Пн–Ср 11:00–22:00 · Чт 11:00–23:00 · Нд вихідний» */
function scheduleSummary(text, t) {
  const hours = parseSchedule(text);
  if (!hours) return null;
  const perDay = [1, 2, 3, 4, 5, 6, 7].map((day) =>
    hours.filter((h) => h.day === day).map((h) => (h.open === '00:00' && h.close === '24:00' ? t('venues.allDay') : `${h.open}–${h.close}`)).join(', ') || t('venues.dayOff'));
  const groups = [];
  perDay.forEach((value, i) => {
    const last = groups[groups.length - 1];
    if (last && last.value === value) last.to = i;
    else groups.push({ from: i, to: i, value });
  });
  const day = (i) => t(`venues.day${i + 1}`);
  return groups.map((g) => `${g.from === g.to ? day(g.from) : `${day(g.from)}–${day(g.to)}`} ${g.value}`).join(' · ');
}

const LOGO_SIDE = 512;

/**
 * Логотип для приложения: PNG не больше 512 px (iPhone не показывает SVG, а большие картинки грузятся долго).
 * Заодно проверяем, не светлый ли он на прозрачном фоне — на белой плитке такой не будет видно.
 */
async function prepareLogo(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((resolve, reject) => {
      const el = new Image();
      el.onload = () => resolve(el);
      el.onerror = () => reject(new Error('image'));
      el.src = url;
    });
    const w = img.naturalWidth || LOGO_SIDE;
    const h = img.naturalHeight || LOGO_SIDE;
    const scale = Math.min(1, LOGO_SIDE / Math.max(w, h));
    const isSvg = file.type === 'image/svg+xml';
    // SVG рисуем в полный размер, растровые — не увеличиваем
    const k = isSvg ? LOGO_SIDE / Math.max(w, h) : scale;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(w * k));
    canvas.height = Math.max(1, Math.round(h * k));
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    let transparent = 0, opaque = 0, light = 0;
    for (let i = 0; i < data.length; i += 16) {
      if (data[i + 3] < 30) { transparent += 1; continue; }
      opaque += 1;
      if (0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2] > 215) light += 1;
    }
    const total = transparent + opaque;
    const lightOnTransparent = total > 0 && transparent / total > 0.3 && opaque > 0 && light / opaque > 0.7;

    const dataUrl = canvas.toDataURL('image/png');
    return { data: dataUrl.split(',')[1], mime: 'image/png', preview: dataUrl, lightOnTransparent };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function readAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/** Заклады компании: как они выглядят в приложении */
export default function VenuesPage() {
  const { t } = useI18n();
  const { canEdit, company } = useSession();
  const [venues, setVenues] = useState(null);
  const [feature, setFeature] = useState(null);
  const [savingFeature, setSavingFeature] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [editing, setEditing] = useState(null);
  const [resetting, setResetting] = useState(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);
  const [merging, setMerging] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get('/admin/restaurants');
      const list = (res.data || []).slice().sort((a, b) => (a.display_name || a.name).localeCompare(b.display_name || b.name));
      setVenues(list);
      setVersion(Date.now());
    } catch (err) {
      setVenues([]);
      toast.error(errorText(err, t('common.error')));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  // Вкладка «Заклади» в застосунку сотрудников
  useEffect(() => {
    if (!company?.id) return;
    api.get(`/admin/tenants/${company.id}`)
      .then((res) => setFeature(Boolean(res.data.data?.venues_enabled)))
      .catch(() => setFeature(false));
  }, [company?.id]);

  const toggleFeature = async (enabled) => {
    setSavingFeature(true);
    try {
      await api.put(`/admin/tenants/${company.id}/venues-feature`, { enabled });
      setFeature(enabled);
      toast.success(t('common.saved'));
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSavingFeature(false);
    }
  };

  const reset = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/restaurants/${resetting.id}`);
      toast.success(t('venues.resetDone'));
      setResetting(null);
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setBusy(false);
    }
  };

  const unmerge = async (name) => {
    try {
      await api.post('/admin/restaurants/unmerge', { name });
      toast.success(t('venues.unmerged'));
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  };

  const setService = async (name, isService) => {
    try {
      await api.post('/admin/restaurants/service', { name, is_service: isService });
      toast.success(isService ? t('venues.markedService') : t('venues.unmarkedService'));
      load();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    }
  };

  if (!venues) return <LoadingBlock />;

  // Основные заведения; прикреплённые названия из кассы — под своим заведением; «службові» — отдельно
  const aliasesOf = new Map();
  for (const v of venues) {
    if (v.merged_into) aliasesOf.set(v.merged_into, [...(aliasesOf.get(v.merged_into) || []), v]);
  }
  const mains = venues.filter((v) => !v.merged_into && !v.is_service);
  const service = venues.filter((v) => v.is_service);
  const txTotal = (v) => (v.tx_count || 0) + (v.id ? (aliasesOf.get(v.id) || []).reduce((n, a) => n + (a.tx_count || 0), 0) : 0);

  const q = query.trim().toLowerCase();
  const matches = (v) => !q || `${v.name} ${v.display_name || ''} ${(aliasesOf.get(v.id) || []).map((a) => a.name).join(' ')}`.toLowerCase().includes(q);
  const visible = (filter === 'service' ? service : mains).filter((v) => {
    if (filter === 'configured' && v.is_new) return false;
    if (filter === 'new' && !v.is_new) return false;
    return matches(v);
  });
  const newCount = mains.filter((v) => v.is_new).length;

  return (
    <>
      <PageHeader title={t('venues.title')} description={t('venues.subtitle')} />

      {feature !== null && (
        <Card className="mb-5">
          <CardBody className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <Smartphone className="mt-0.5 h-5 w-5 shrink-0 text-brand-600" />
              <div>
                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('venues.tabTitle')}</p>
                <p className="mt-0.5 max-w-2xl text-xs text-zinc-500">{feature ? t('venues.tabOnHint') : t('venues.tabOffHint')}</p>
              </div>
            </div>
            <Switch checked={feature} onChange={toggleFeature} disabled={!canEdit || savingFeature} label={t('venues.tabTitle')} />
          </CardBody>
        </Card>
      )}

      {feature && company?.id && <TopVenueCard tenantId={company.id} canEdit={canEdit} version={version} />}

      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <Tabs
          className="flex-1"
          value={filter}
          onChange={setFilter}
          items={[
            { id: 'all', label: t('venues.filters.all'), count: mains.length },
            { id: 'configured', label: t('venues.filters.configured'), count: mains.length - newCount },
            { id: 'new', label: t('venues.filters.new'), count: newCount },
            { id: 'service', label: t('venues.filters.service'), count: service.length },
          ]}
        />
        <div className="sm:w-64">
          <Input icon={Search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('venues.search')} aria-label={t('venues.search')} />
        </div>
      </div>

      {filter === 'service' && <p className="-mt-2 mb-4 text-sm text-zinc-500">{t('venues.serviceHint')}</p>}

      {!visible.length ? (
        <Card><EmptyState icon={Store} title={filter === 'service' ? t('venues.serviceEmpty') : t('venues.empty')} text={filter === 'service' ? null : t('venues.emptyHint')} /></Card>
      ) : filter === 'service' ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((v) => (
            <Card key={v.id ?? `s:${v.name}`}>
              <CardBody className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  <Building2 className="h-5 w-5 shrink-0 text-zinc-400" />
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{v.name}</p>
                    <p className="text-xs text-zinc-500">{t('venues.txCount', { count: v.tx_count || 0 })}</p>
                  </div>
                </div>
                {canEdit && <Button variant="ghost" size="sm" icon={Undo2} onClick={() => setService(v.name, false)}>{t('venues.unmarkService')}</Button>}
              </CardBody>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {visible.map((v) => (
            <Card key={v.id ?? `new:${v.name}`} className="flex flex-col">
              <div className="flex flex-1 items-start gap-3 p-4">
                <VenueLogo venue={v} version={version} />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-zinc-900 dark:text-zinc-100">{v.display_name || v.name}</p>
                  {v.display_name && v.display_name !== v.name && <p className="truncate text-xs text-zinc-500">{t('venues.fromPos')}: {v.name}</p>}
                  <p className={clsx('mt-2 flex items-start gap-1.5 text-xs', v.schedule ? 'text-zinc-600 dark:text-zinc-400' : 'text-zinc-400')}>
                    <Clock className="mt-px h-3.5 w-3.5 shrink-0" />
                    <span className="line-clamp-2">{v.schedule || '—'}</span>
                  </p>
                  <p className={clsx('mt-1 flex items-start gap-1.5 text-xs', v.address ? 'text-zinc-600 dark:text-zinc-400' : 'text-zinc-400')}>
                    <MapPin className="mt-px h-3.5 w-3.5 shrink-0" />
                    <span className="line-clamp-2">{v.address || t('venues.noAddress')}</span>
                  </p>
                  <p className="mt-1 flex items-start gap-1.5 text-xs text-zinc-500">
                    <Receipt className="mt-px h-3.5 w-3.5 shrink-0" />
                    <span>{t('venues.txCount', { count: txTotal(v) })}</span>
                  </p>
                  {v.id && aliasesOf.get(v.id)?.length > 0 && (
                    <div className="mt-2.5">
                      <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-zinc-400">{t('venues.alsoInPos')}</p>
                      <div className="flex flex-wrap gap-1">
                        {aliasesOf.get(v.id).map((a) => (
                          <span key={a.name} className="inline-flex items-center gap-1 rounded-md bg-zinc-100 py-0.5 pl-2 pr-1 text-xs text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                            {a.name} <span className="tabular text-zinc-400">· {a.tx_count || 0}</span>
                            {canEdit && (
                              <button type="button" onClick={() => unmerge(a.name)} className="rounded p-0.5 text-zinc-400 hover:bg-zinc-200 hover:text-zinc-700 dark:hover:bg-zinc-700" aria-label={t('venues.unmerge', { name: a.name })}>
                                <X className="h-3 w-3" />
                              </button>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 border-t border-zinc-100 px-4 py-2 dark:border-zinc-800">
                {v.is_new
                  ? <Badge tone="warning" dot>{t('venues.notConfigured')}</Badge>
                  : v.show_in_app
                    ? <Badge tone="success" dot>{t('venues.inApp')}</Badge>
                    : <Badge tone="neutral"><EyeOff className="mr-1 h-3 w-3" />{t('venues.hidden')}</Badge>}
                {canEdit && (
                  <span className="ml-auto flex items-center gap-0.5">
                    {v.is_new && <Button variant="ghost" size="icon-sm" icon={Building2} aria-label={t('venues.markService')} title={t('venues.markService')} onClick={() => setService(v.name, true)} />}
                    {!v.is_new && <Button variant="ghost" size="icon-sm" icon={GitMerge} aria-label={t('venues.merge')} title={t('venues.merge')} onClick={() => setMerging(v)} />}
                    {!v.is_new && <Button variant="ghost" size="icon-sm" icon={RotateCcw} aria-label={t('venues.reset')} title={t('venues.reset')} onClick={() => setResetting(v)} />}
                    <Button variant="ghost" size="sm" icon={Pencil} onClick={() => setEditing(v)}>{t('common.edit')}</Button>
                  </span>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      <VenueModal venue={editing} version={version} onClose={() => setEditing(null)} onSaved={load} />
      <MergeModal target={merging} candidates={mains.filter((v) => v.name !== merging?.name)} onClose={() => setMerging(null)} onSaved={load} />
      <ConfirmDialog
        open={!!resetting}
        onClose={() => setResetting(null)}
        onConfirm={reset}
        loading={busy}
        title={t('venues.resetTitle')}
        text={t('venues.resetText', { name: resetting?.display_name || resetting?.name })}
        confirmLabel={t('venues.reset')}
      />
    </>
  );
}

function VenueLogo({ venue, version, src, size = 'h-12 w-12' }) {
  const [failed, setFailed] = useState(false);
  const url = src ?? (venue?.logo_url ? `${venue.logo_url}${venue.logo_url.includes('?') ? '&' : '?'}t=${version}` : null);
  useEffect(() => { setFailed(false); }, [url]);
  return (
    <span className={clsx('flex shrink-0 items-center justify-center overflow-hidden rounded-xl border border-zinc-200 bg-zinc-50 dark:border-zinc-700 dark:bg-zinc-800', size)}>
      {url && !failed
        ? <img src={url} alt="" className="h-full w-full object-contain" onError={() => setFailed(true)} />
        : <Store className="h-5 w-5 text-zinc-400" />}
    </span>
  );
}

function VenueModal({ venue, version, onClose, onSaved }) {
  const { t } = useI18n();
  const [form, setForm] = useState(null);
  const [logo, setLogo] = useState(null); // { data, mime, preview } | 'delete' | null
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    if (!venue) return;
    setForm({
      display_name: venue.display_name || venue.name,
      schedule: venue.schedule || '',
      servio_id: venue.servio_id ?? '',
      category: venue.category || '',
      address: venue.address || '',
      coords: venue.latitude != null && venue.longitude != null ? `${venue.latitude}, ${venue.longitude}` : '',
      phone: venue.phone || '',
      description: venue.description || '',
      menu_url: venue.menu_url || '',
      show_in_app: venue.is_new ? true : venue.show_in_app !== 0 && venue.show_in_app !== false,
      sort_order: venue.sort_order ?? '',
    });
    setLogo(null);
  }, [venue]);

  const pick = async (file) => {
    if (!file) return;
    if (file.size > MAX_LOGO) {
      toast.error(t('venues.logoTooBig'));
      return;
    }
    try {
      const prepared = await prepareLogo(file);
      if (prepared.lightOnTransparent) toast(t('venues.logoLightWarning'), { icon: '⚠️', duration: 7000 });
      setLogo({ data: prepared.data, mime: prepared.mime, preview: prepared.preview });
    } catch {
      // Браузер не смог прочитать картинку — загружаем как есть
      const data = await readAsBase64(file);
      setLogo({ data, mime: file.type, preview: URL.createObjectURL(file) });
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  // Координаты одной строкой «50.4501, 30.5234» — как копируются из Google Maps
  const parseCoords = (text) => {
    const m = text.trim().match(/^(-?\d+(?:\.\d+)?)\s*[,;\s]\s*(-?\d+(?:\.\d+)?)$/);
    return m ? { latitude: Number(m[1]), longitude: Number(m[2]) } : null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const coords = form.coords.trim() ? parseCoords(form.coords) : { latitude: '', longitude: '' };
    if (!coords) {
      toast.error(t('venues.coordsInvalid'));
      return;
    }
    setSaving(true);
    try {
      await api.post('/admin/restaurants', {
        name: venue.name,
        display_name: form.display_name.trim(),
        schedule: form.schedule.trim(),
        servio_id: form.servio_id === '' ? undefined : Number(form.servio_id),
        category: form.category.trim(),
        address: form.address.trim(),
        latitude: coords.latitude,
        longitude: coords.longitude,
        phone: form.phone.trim(),
        description: form.description.trim(),
        menu_url: form.menu_url.trim(),
        show_in_app: form.show_in_app,
        sort_order: form.sort_order === '' ? '' : Number(form.sort_order),
        ...(logo && logo !== 'delete' ? { logo_data: logo.data, logo_mime: logo.mime } : {}),
        ...(logo === 'delete' ? { delete_logo: true } : {}),
      });
      toast.success(t('venues.saved'));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const hasLogo = logo === 'delete' ? false : !!(logo || venue?.logo_url);
  const preview = logo === 'delete' ? null : logo?.preview;

  return (
    <Modal
      open={!!venue && !!form}
      onClose={onClose}
      title={venue?.display_name || venue?.name}
      description={venue ? `${t('venues.fromPos')}: ${venue.name}` : undefined}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button type="submit" form="venue-form" loading={saving}>{t('common.save')}</Button>
        </>
      }
    >
      {form && (
        <form id="venue-form" onSubmit={submit} className="space-y-4">
          <Field label={t('venues.logo')} hint={t('venues.logoHint')}>
            <div className="flex items-center gap-4">
              {logo === 'delete' ? <VenueLogo src="" size="h-16 w-16" /> : <VenueLogo venue={venue} version={version} src={preview} size="h-16 w-16" />}
              <div className="flex flex-wrap gap-2">
                <input ref={fileRef} type="file" accept={LOGO_TYPES} className="sr-only" onChange={(e) => pick(e.target.files?.[0])} />
                <Button variant="secondary" size="sm" icon={ImagePlus} onClick={() => fileRef.current?.click()}>{t('venues.uploadLogo')}</Button>
                {hasLogo && <Button variant="danger-ghost" size="sm" icon={Trash2} onClick={() => setLogo(venue?.logo_url ? 'delete' : null)}>{t('venues.removeLogo')}</Button>}
              </div>
            </div>
          </Field>
          <Field label={t('venues.displayName')} required>
            <Input value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} maxLength={200} required />
          </Field>
          <Field
            label={t('venues.schedule')}
            hint={form.schedule.trim()
              ? (scheduleSummary(form.schedule, t) ? `${t('venues.scheduleUnderstood')}: ${scheduleSummary(form.schedule, t)}` : t('venues.scheduleNotUnderstood'))
              : t('venues.scheduleHint')}
          >
            <Input value={form.schedule} onChange={(e) => setForm({ ...form, schedule: e.target.value })} maxLength={500} placeholder={t('venues.schedulePlaceholder')} />
          </Field>
          <Field label={t('venues.category')}>
            <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} maxLength={60} placeholder={t('venues.categoryPlaceholder')} />
          </Field>
          <Field label={t('venues.address')}>
            <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} maxLength={300} placeholder={t('venues.addressPlaceholder')} />
          </Field>
          <Field label={t('venues.coords')} hint={t('venues.coordsHint')}>
            <div className="flex items-center gap-2">
              <Input value={form.coords} onChange={(e) => setForm({ ...form, coords: e.target.value })} placeholder="50.4501, 30.5234" />
              {parseCoords(form.coords) && (
                <a className="shrink-0 text-xs font-medium text-brand-700 hover:underline dark:text-brand-300" target="_blank" rel="noreferrer"
                  href={`https://www.google.com/maps?q=${encodeURIComponent(form.coords.trim())}`}>{t('venues.openMap')}</a>
              )}
            </div>
          </Field>
          <Field label={t('venues.phone')}>
            <Input type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} maxLength={40} placeholder="+380 44 000 00 00" />
          </Field>
          <Field label={t('venues.menuUrl')} hint={t('venues.menuUrlHint')}>
            <div className="flex items-center gap-2">
              <Input type="url" value={form.menu_url} onChange={(e) => setForm({ ...form, menu_url: e.target.value })} maxLength={500} placeholder="https://…" />
              {/^https?:\/\//i.test(form.menu_url.trim()) && (
                <a className="shrink-0 text-xs font-medium text-brand-700 hover:underline dark:text-brand-300" target="_blank" rel="noreferrer" href={form.menu_url.trim()}>{t('venues.openMenu')}</a>
              )}
            </div>
          </Field>
          <Field label={t('venues.description')}>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={1000} placeholder={t('venues.descriptionPlaceholder')} />
          </Field>
          <div className="flex items-center justify-between gap-4 rounded-lg border border-zinc-200 px-3 py-2.5 dark:border-zinc-700">
            <div>
              <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{t('venues.showInApp')}</p>
              <p className="text-xs text-zinc-500">{t('venues.showInAppHint')}</p>
            </div>
            <Switch checked={form.show_in_app} onChange={(v) => setForm({ ...form, show_in_app: v })} label={t('venues.showInApp')} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t('venues.sortOrder')} hint={t('venues.sortOrderHint')}>
              <Input type="number" min="0" max="9999" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} />
            </Field>
            <Field label={t('venues.servioId')}>
              <Input type="number" min="1" value={form.servio_id} onChange={(e) => setForm({ ...form, servio_id: e.target.value })} />
            </Field>
          </div>
        </form>
      )}
    </Modal>
  );
}

/** Прикрепить названия из кассы к заведению: операции, визиты и аналитика считаются вместе */
function MergeModal({ target, candidates, onClose, onSaved }) {
  const { t, fmt } = useI18n();
  const [picked, setPicked] = useState(new Set());
  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setPicked(new Set()); setQuery(''); }, [target]);

  const toggle = (name) => {
    const next = new Set(picked);
    next.has(name) ? next.delete(name) : next.add(name);
    setPicked(next);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/admin/restaurants/merge', { target_id: target.id, names: [...picked] });
      toast.success(t('venues.merged', { count: picked.size }));
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errorText(err, t('common.error')));
    } finally {
      setSaving(false);
    }
  };

  const q = query.trim().toLowerCase();
  const list = candidates
    .filter((c) => !q || `${c.name} ${c.display_name || ''}`.toLowerCase().includes(q))
    .sort((a, b) => (b.tx_count || 0) - (a.tx_count || 0));

  return (
    <Modal
      open={!!target}
      onClose={onClose}
      title={t('venues.mergeTitle', { name: target?.display_name || target?.name })}
      description={t('venues.mergeHint')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button icon={GitMerge} onClick={save} loading={saving} disabled={!picked.size}>{t('venues.mergeConfirm', { count: picked.size })}</Button>
        </>
      }
    >
      <div className="space-y-3">
        <Input icon={Search} value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('venues.search')} aria-label={t('venues.search')} />
        <div className="max-h-80 divide-y divide-zinc-100 overflow-y-auto rounded-lg border border-zinc-200 dark:divide-zinc-800 dark:border-zinc-700">
          {list.map((c) => (
            <label key={c.name} className="flex cursor-pointer items-center gap-3 px-3 py-2.5 hover:bg-zinc-50 dark:hover:bg-zinc-800/50">
              <input type="checkbox" checked={picked.has(c.name)} onChange={() => toggle(c.name)} className="h-4 w-4 rounded border-zinc-300 text-brand-600 focus:ring-brand-500" />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-zinc-900 dark:text-zinc-100">{c.name}</p>
                {c.display_name && c.display_name !== c.name && <p className="truncate text-xs text-zinc-500">{c.display_name}</p>}
              </div>
              <div className="text-right text-xs text-zinc-500">
                <p className="tabular">{t('venues.txCount', { count: c.tx_count || 0 })}</p>
                {c.last_tx && <p>{t('venues.lastTx', { date: fmt.date(c.last_tx) })}</p>}
              </div>
            </label>
          ))}
          {!list.length && <p className="px-3 py-6 text-center text-sm text-zinc-500">{t('venues.empty')}</p>}
        </div>
      </div>
    </Modal>
  );
}
